import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useState } from "react";
import { LogOut, Users, UserCircle, HelpCircle, CreditCard, Building2, Menu, X } from "lucide-react";

const tabs = [
  { to: "/crm/first-steps", label: "CRM", icon: Users },
  { to: "/crm/billing", label: "Billing", icon: CreditCard },
  { to: "/crm/account", label: "Account", icon: UserCircle },
  { to: "/crm/help", label: "Help", icon: HelpCircle },
];

// Direct-link shortcuts shown only to specific operators in the CRM header.
// Keeps the main nav uncluttered while giving the site owner one-click access
// to the Admin → Organizations tab (otherwise 3 clicks away: main site → Admin → Organizations).
const SHORTCUT_EMAILS = new Set(["thedisciplelifems@gmail.com"]);

export default function CrmLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const handleLogout = () => {
    logout();
    navigate("/crm/login");
  };

  const firstName = user?.name?.split(" ")?.[0] || user?.email?.split("@")?.[0];

  const location = useLocation();
  const CRM_PATHS = ["/crm", "/crm/first-steps", "/crm/next-steps", "/crm/teachers", "/crm/team-activity", "/crm/team"];
  const isCrmActive = CRM_PATHS.includes(location.pathname);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-base)" }} data-testid="crm-layout">
      <header
        className="border-b sticky top-0 z-30 backdrop-blur"
        style={{
          borderColor: "var(--border-subtle)",
          background: "color-mix(in srgb, var(--bg-alt) 88%, transparent)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 md:py-4 flex items-center justify-between gap-4">
          <Link to="/crm" data-testid="crm-brand" className="flex flex-col leading-tight">
            <span className="font-serif-display text-xl lg:text-2xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>
              thedisciple<span style={{ color: "var(--gold)" }}>.tracker</span>
            </span>
            <span className="text-[0.55rem] uppercase tracking-[0.32em] mt-0.5 font-semibold" style={{ color: "var(--muted)" }}>
              Your Church CRM
            </span>
          </Link>

          {user && (
            <button onClick={() => setMenuOpen((o) => !o)} className="md:hidden w-10 h-10 rounded-full flex items-center justify-center" style={{ border: "1px solid var(--border-strong)", color: "var(--ink)" }} aria-label="Menu" data-testid="crm-mobile-menu-toggle">
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          )}
          {user && (
            <div className="hidden md:flex items-center gap-4 flex-wrap">
              <nav className="flex items-center gap-1 flex-wrap" data-testid="crm-tabs">
                {tabs.map((t) => {
                  const Icon = t.icon;
                  const forceActive = t.label === "CRM" ? isCrmActive : undefined;
                  return (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      end={t.label !== "CRM"}
                      data-testid={`crm-tab-${t.label.toLowerCase()}`}
                      className={({ isActive }) => {
                        const active = forceActive !== undefined ? forceActive : isActive;
                        return `flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors ${
                          active
                            ? "bg-[var(--ink)] text-[var(--bg-base)]"
                            : "text-[var(--ink-2)] hover:bg-[var(--surface-hover)]"
                        }`;
                      }}
                    >
                      <Icon size={14} />
                      {t.label}
                    </NavLink>
                  );
                })}
                {user.is_admin && SHORTCUT_EMAILS.has((user.email || "").toLowerCase()) && (
                  <Link
                    to="/admin?tab=orgs"
                    data-testid="crm-shortcut-admin-orgs"
                    title="Jump to Admin → Organizations"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors"
                    style={{
                      color: "var(--gold)",
                      border: "1px solid var(--gold)",
                      background: "transparent",
                    }}
                  >
                    <Building2 size={14} />
                    Organizations
                  </Link>
                )}
              </nav>
              <div className="flex items-center gap-3 pl-4 border-l" style={{ borderColor: "var(--border-subtle)" }}>
                {firstName && (
                  <span className="hidden sm:inline text-xs" style={{ color: "var(--muted)" }} data-testid="crm-user-name">
                    Hi, <span style={{ color: "var(--ink)" }}>{firstName}</span>
                  </span>
                )}
                <button
                  onClick={handleLogout}
                  data-testid="crm-logout-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors hover:bg-[var(--surface-hover)]"
                  style={{ color: "var(--terracotta)" }}
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
        {user && menuOpen && (
          <div className="md:hidden border-t px-6 py-3 flex flex-col gap-1" style={{ borderColor: "var(--border-subtle)" }} data-testid="crm-mobile-menu">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink key={t.to} to={t.to} end={t.label !== "CRM"} onClick={() => setMenuOpen(false)} className={({ isActive }) => `flex items-center gap-2 px-3 py-2.5 rounded-md text-sm ${(t.label === "CRM" ? isCrmActive : isActive) ? "bg-[var(--surface-hover)] font-semibold" : ""}`} style={{ color: "var(--ink-2)" }} data-testid={`crm-mobile-tab-${t.label.toLowerCase()}`}>
                  <Icon size={15} /> {t.label}
                </NavLink>
              );
            })}
            {user.is_admin && SHORTCUT_EMAILS.has((user.email || "").toLowerCase()) && (
              <Link to="/admin?tab=orgs" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm" style={{ color: "var(--gold)" }} data-testid="crm-mobile-admin-orgs"><Building2 size={15} /> Organizations</Link>
            )}
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-left" style={{ color: "var(--terracotta)" }} data-testid="crm-mobile-logout"><LogOut size={15} /> Sign out{firstName ? ` (${firstName})` : ""}</button>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t py-4 text-xs text-center" style={{ borderColor: "var(--border-subtle)", color: "var(--muted)" }}>
        © {new Date().getFullYear()} The Disciple Life · thedisciple.tracker
      </footer>
    </div>
  );
}
